import React from "react";
import { createTheme, styled, lighten, alpha, darken, SimplePaletteColorOptions } from '@mui/material/styles';
import { Box, ButtonGroup, TextField, Chip, Accordion, LinearProgress, Link as MuiLink, Typography, Switch, Stepper, Step } from '@mui/material';
import { TablePaginationActionsProps } from '@mui/material/TablePaginationActions';
import Remove from "@mui/icons-material/Remove";
import CheckIcon from "@mui/icons-material/Check";
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';

const UncheckedRounded = styled('span')({
	width: 18,
	height: 18,
	borderRadius: 6,
	border: '1.5px solid #999',
	backgroundColor: 'transparent',
});

const CheckedRounded = styled('span')(({ theme }) => ({
	width: 18,
	height: 18,
	borderRadius: 6,
	border: `1.5px solid ${theme.palette.primary.main}`,
	backgroundColor: '#fff',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	'& svg': {
		fontSize: 14,
		color: theme.palette.primary.main,
	},
}));

const CheckedRoundedIcon = () => <CheckedRounded><CheckIcon /></CheckedRounded>;
const IndeterminateRoundedIcon = () => <CheckedRounded><Remove /></CheckedRounded>;

type ThemeTablePaginationActionsProps = Pick<TablePaginationActionsProps, 'count' | 'page' | 'rowsPerPage' | 'onPageChange'>;

export function ThemeTablePaginationActions(props: ThemeTablePaginationActionsProps){
	const { count, page, rowsPerPage, onPageChange } = props;
	const handleFirst = (event: React.MouseEvent<HTMLButtonElement>) => { onPageChange(event, 0); };
	const handlePrev = (event: React.MouseEvent<HTMLButtonElement>) => { onPageChange(event, page - 1); };
	const handleNext = (event: React.MouseEvent<HTMLButtonElement>) => { onPageChange(event, page + 1); };
	const handleLast = (event: React.MouseEvent<HTMLButtonElement>) => { onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1)); };
	const iconStyle = { color: '#4F4D55', '.Mui-disabled &': { color: '#BEBEBE' } };
	return (
		<div style={{whiteSpace: 'nowrap'}}>
			<IconButton onClick={handleFirst} disabled={page === 0} aria-label="First page" title="First page" size="large"><FirstPageIcon sx={iconStyle}/></IconButton>
			<IconButton onClick={handlePrev} disabled={page === 0} aria-label="Previous page" title="Previous page" size="large"><KeyboardArrowLeft sx={iconStyle}/></IconButton>
			<IconButton onClick={handleNext} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="Next page" title="Next page" size="large"><KeyboardArrowRight sx={iconStyle}/></IconButton>
			<IconButton onClick={handleLast} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="Last page" title="Last page" size="large"><LastPageIcon sx={iconStyle}/></IconButton>
		</div>
	);
}

const palette: Record<'primary' | 'secondary' | 'tertiary' | 'error' | 'textPrimary' | 'textSecondary', SimplePaletteColorOptions> = {
	primary: {
		main: '#006BFF'
	},
	secondary: {
		main: '#006BFF',
	},
	tertiary: {
		main: '#006BFF'
	},
	error: {
		main: '#f44336'
	},
	textPrimary: {
		main: "#0A090B"
	},
	textSecondary: {
		main: '#4F4D55'
	}
};

export const buttonHeight = "38px";
export const headerSize = "24px";
export const cardHeaderSize = "22px";
export const dialogHeaderSize = "22px";
export const inputHeight = "38px";
export const inputFontSize = "16px";

const theme = createTheme({
	typography: {
		fontFamily: "Poppins, Helvetica, Arial, sans-serif",
		h6: {
			fontWeight: 'bold',
			fontSize: '13pt'
		},
		body1: {
			fontSize: '0.875rem'
		}
	},
	palette: palette,
	components: {
		MuiCheckbox: {
			defaultProps: {
				icon: <UncheckedRounded />,
				checkedIcon: <CheckedRoundedIcon />,
				indeterminateIcon: <IndeterminateRoundedIcon />,
				size: 'medium'
			},
			styleOverrides: {
				root: {
					"&.Mui-checked": {},
					"& .MuiSvgIcon-root": {
						fontSize: "14px",
					},
				}
			}
		},
		MuiInput: {
			styleOverrides: {
				root: {
					fontSize: inputFontSize,
					minHeight: inputHeight,
					height: inputHeight,
					"&.Mui-disabled": {
						color: 'rgba(0, 0, 0, 0.87)',
						'&:before': {
							borderBottom: 'none !important',
							borderBottomStyle: 'none !important'
						},
						"&.Mui-focused": {
							borderColor: "black"
						}
					},
					"&.MuiInput-underline": {
						"&:after": {
							borderColor: 'black'
						}
					}
				},
				input: {
					height: 'auto',
					padding: '8px 0',
					"&.Mui-disabled": {
						color: 'rgba(0, 0, 0, 0.87)',
						WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)'
					}
				}
			}
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					fontSize: inputFontSize,
					lineHeight: '1.1876em',
					minHeight: inputHeight,
					height: inputHeight
				},
				input: {
					height: 'auto',
					padding: '8px 0',
					"&.Mui-disabled": {
						color: 'rgba(0, 0, 0, 0.87)',
						WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)'
					}
				}
			}
		},
		MuiOutlinedInput: {
			styleOverrides: {
				notchedOutline: {
					borderRadius: '6px'
				},
				root: {
					backgroundColor: 'white',
					minHeight: inputHeight,
					height: inputHeight,
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: "black"
					},
					"&.Mui-error .MuiOutlinedInput-notchedOutline": {
						borderColor: "#f44336",
						borderWidth: "2px"
					},
					"&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
						borderColor: "#f44336",
						borderWidth: "2px"
					},
					borderRadius: '6px'
				},
				input: {
					padding: '8px 14px',
					'&.MuiInputBase-inputMultiline': {
						padding: 0
					}
				}
			}
		},
		MuiTablePagination: {
			defaultProps: {
				ActionsComponent: ThemeTablePaginationActions
			},
			styleOverrides: {
				root: {
					minHeight: '52px'
				},
				toolbar: {
					minHeight: '52px'
				},
				select: {
					backgroundColor: '#fff'
				}
			}
		},
		MuiTextField: {
			defaultProps: {
				variant: 'standard'
			}
		},
		MuiSelect: {
			defaultProps: {
				variant: 'standard'
			},
			styleOverrides: {
				select: {
					minHeight: '1.1876em'
				}
			}
		},
		MuiFormControl: {
			defaultProps: {
				variant: 'standard'
			},
			styleOverrides: {
				root: {
					marginTop: 0,
					marginBottom: 0
				}
			}
		},
		MuiModal: {
			styleOverrides: {
				root: {
					"& .lbtoolbar": {
						backgroundColor: "green",
						color: 'black'
					}
				}
			}
		},
		MuiDialog: {
			styleOverrides: {
				root: {
					"& .MuiToolbar-root.lbtoolbar": {
						backgroundColor: "white !important",
						color: 'black !important',
						"& .MuiIconButton-root": {
							color: 'black'
						},
						fontSize: '22px !important'
					},
				},
				paper: {
					borderRadius: '4px'
				}
			}
		},
		MuiInputLabel: {
			styleOverrides: {
				shrink: {
					transform: "translate(0, 4px) scale(0.75)",
					"&.MuiInputLabel-outlined": {
						transform: "translate(14px, -6px) scale(0.75)"
					}
				},
				root: {
					overflow: 'visible',
					"&.Mui-focused:not(.Mui-error)": {
						color: 'black'
					}
				}
			}
		},
		MuiFormLabel: {
			styleOverrides: {
				root: {
					"&.Mui-disabled": {
						color: 'rgba(0, 0, 0, 0.54)'
					},
					fontSize: '1rem',
					lineHeight: 1
				}
			}
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					"&.Mui-error": {
						whiteSpace: "pre-wrap"
					}
				}
			}
		},
		MuiCardContent: {
			styleOverrides: {
				root: {
					padding: '16px 24px 24px',
					"&:last-child": {
						paddingBottom: '16px'
					}
				}
			}
		},
		MuiToolbar: {
			styleOverrides: {
				root: {
					fontFamily: 'Poppins, Helvetica, Arial, sans-serif',
					"&.lbtoolbar": {
						backgroundColor: 'rgb(32, 32, 32) !important',
						color: 'white !important',
						fontWeight: '700 !important',
						fontSize: '13pt !important',
						flex: 1
					}
				}
			}
		},
		MuiAccordionSummary: {
			styleOverrides: {
				root: {
					padding: 0,
					margin: 0,
					minHeight: '0 !important',
					overflow: 'hidden',
					'&.Mui-expanded': {
						margin: 0,
						minHeight: 0
					}
				},
				content: {
					margin: '0 !important',
					minHeight: '0 !important'
				},
				expandIconWrapper: {
					color: 'white',
					position: 'absolute',
					right: '8px',
					marginRight: '0'
				}
			}
		},
		MuiDialogContent: {
			styleOverrides: {
				root: {
					maxHeight: "calc(100vh - 250px)",
					padding: '1rem 24px 24px'
				}
			}
		},
		MuiButton: {
			defaultProps: {
				color: 'inherit',
				variant: 'outlined'
			},
			styleOverrides: {
				root: {
					height: buttonHeight,
					boxSizing: 'border-box',
					lineHeight: '1.5',
					textTransform: 'none'
				},
				text: {
					padding: '0 16px'
				},
				sizeSmall: {
					padding: '0 8px'
				},
				outlined: {
					padding: '0 16px',
					borderRadius: '6px',
					overflow: 'hidden',
					border: '1px solid #4F4D55',
					color: '#0A090B',
					transition: 'background-color 150ms ease',
					"&:hover": {
						backgroundColor: 'rgba(0, 0, 0, 0.04)',
					},
					'&.Mui-disabled': {
						border: '1px solid rgba(0, 0, 0, 0.26)'
					},
				},
				contained: {
					padding: '0 16px',
					borderRadius: '6px',
					overflow: 'hidden',
					boxShadow: 'none',
					"&:hover": {
						boxShadow: 'none'
					},
					"&:active": {
						boxShadow: 'none'
					}
				},
				containedPrimary: {
					border: '1px solid',
					borderColor: palette.primary.main,
					transition: 'filter 0.3s ease-in-out',
					"&:hover": {
						filter: 'brightness(1.6)',
						backgroundColor: palette.primary.main,
					},
					'&.Mui-disabled': {
						backgroundColor: 'rgba(0, 0, 0, 0.12)',
						borderColor: 'rgba(0, 0, 0, 0)'
					}
				},
				containedError: {
					border: 'none',
					borderColor: palette.error.main,
					backgroundColor: palette.error.main,
					color: 'white',
					transition: 'filter 0.3s ease-in-out',
					"&:hover": {
						filter: 'brightness(1.6)',
						backgroundColor: palette.error.main,
					},
					'&.Mui-disabled': {
						backgroundColor: 'rgba(0, 0, 0, 0.12)',
						borderColor: 'rgba(0, 0, 0, 0)'
					}
				},
				outlinedPrimary: {
					borderColor: alpha(palette.primary.main, 0.5),
					color: palette.primary.main,
					transition: 'filter 0.3s ease-in-out, background-color 150ms ease, border-color 150ms ease',
					"&:hover": {
						backgroundColor: alpha(palette.primary.main, 0.08),
						borderColor: palette.primary.dark || darken(palette.primary.main, 0.2)
					},
				},
				outlinedError: {
					borderColor: palette.error.main,
					color: palette.error.main,
					transition: 'all 0.3s ease-in-out',
					"&:hover": {
						backgroundColor: palette.error.main,
						color: 'white',
					}
				}
			}
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					height: '48px',
					"&.Mui-selected": {
						backgroundColor: 'rgba(0, 0, 0, 0.04) !important'
					},
					"& .MuiButton-root": {
						padding: '0 16px',
						"&:hover": {
							backgroundColor: '#e8e8e8'
						}
					},
					"& .MuiButton-containedPrimary:hover": {
						backgroundColor: palette.primary.dark || '#0056cc',
						borderColor: palette.primary.dark || '#0056cc'
					},
					"& .MuiButton-outlinedPrimary:hover": {
						backgroundColor: alpha(palette.primary.main, 0.08),
						borderColor: palette.primary.dark || darken(palette.primary.main, 0.2),
						color: palette.primary.main
					}
				},
				head: {
					height: '56px'
				}
			}
		},
		MuiTableCell: {
			styleOverrides: {
				head: {
					lineHeight: 'inherit',
					color: 'rgba(0, 0, 0, 0.54)',
					fontSize: '0.75rem',
					fontWeight: '500'
				},
				root: {
					fontSize: '13px',
					padding: '4px 56px 4px 24px'
				},
				paddingCheckbox: {
					padding: '0 6px'
				},
				paddingNone: {
					padding: 0
				}
			}
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					fontSize: '1rem',
					minHeight: '48px',
					'@media (min-width: 600px)': {
						minHeight: "48px"
					}
				}
			}
		},
		MuiTabs: {
			styleOverrides: {
				indicator: {
					backgroundColor: palette.primary.main
				}
			}
		},
		MuiTab: {
			styleOverrides: {
				root: {
					color: 'rgba(0, 0, 0, 0.6)',
					minWidth: '160px',
					padding: '12px 16px',
					textTransform: 'uppercase',
					fontSize: '0.875rem',
					'&.Mui-selected': {
						color: 'rgba(0, 0, 0, 0.87)'
					}
				}
			}
		},
		MuiLinearProgress: {
			styleOverrides: {
				root: {
					backgroundColor: lighten(palette.primary.main, 0.62)
				},
				bar: {
					backgroundColor: palette.primary.main
				}
			}
		},
		MuiButtonGroup: {
			styleOverrides: {
				grouped: {
					borderColor: '#4F4D55',
					color: 'rgba(0, 0, 0, 0.87)',
					'&.Mui-disabled': {
						borderColor: 'rgba(0, 0, 0, 0.26)',
						color: 'rgba(0, 0, 0, 0.26)'
					}
				}
			}
		},
		MuiPaginationItem: {
			styleOverrides: {
				root: {
					backgroundColor: 'white',
					'&.Mui-selected': {
						backgroundColor: 'white',
						color: palette.primary.main,
						border: `1px solid ${palette.primary.main}`
					}
				},
				previousNext: {
					color: '#4F4D55',
					'&.Mui-disabled': {
						color: '#BEBEBE'
					}
				}
			}
		}
	}
});

// ─── Constants ───

export const pageContentGap = "16px";
// Used by CatalogManagerResults wrappers; ensure containers span full width
export const defaultResultsContainerStyles = { gap: "16px", width: "100%" };

// ─── Styled Components (merged from catalogmanager/utils/styles.js) ───

export const pocPrimary = "#202020";

// Menu Components
export const MoreOptsMenu = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    background: "white",
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: "6px",
}));

// Switch Components
export const StyledSwitch = styled(Switch)(({ theme }) => ({
    "& .MuiSwitch-switchBase.Mui-checked": {
        color: theme.palette.primary.main,
    },
    "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
        backgroundColor: theme.palette.primary.main,
    }
}));

// Image Components
export const ImageFixedPost = styled(Box)(() => ({
    "& img": {
        width: "100%",
        height: "auto",
    }
}));

// Stepper Components
export const StyledStepper = styled(Stepper)(() => ({
    width: "100%",
}));

export const StyledStep = styled(Step)(({ theme }) => ({
    "& .MuiStepIcon-root.MuiStepIcon-active": {
        color: theme.palette.tertiary?.main || theme.palette.primary.main,
    }
}));

// Progress Bar Components
export const ProgressBar = styled(LinearProgress)(({ theme }) => ({
    backgroundColor: theme.palette.tertiary?.main || theme.palette.primary.main,
}));

export const ProgressBarBackground = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.tertiary?.main || theme.palette.primary.main,
    filter: "brightness(1.6)",
}));

// Text Components
export const DisabledText = styled(Typography)(() => ({
    color: `#00000042`,
}));

export const Link = styled(MuiLink)(() => ({
    color: "#006BFF"
}));

export const SecondaryText = styled(Typography)(({ theme }) => ({
    color: theme.palette.textSecondary?.main || theme.palette.text.secondary
}));

// Button Group Components
export const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
    opacity: 0.5,
    transition: "opacity 0.3s ease-in-out",
    "& .MuiButton-outlined": {
        borderRadius: "6px",
        border: "1px solid #4F4D55",
    },
    "& .MuiButtonGroup-groupedHorizontal:not(:first-child)": {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0
    },
    "& .MuiButtonGroup-groupedHorizontal:not(:last-child)": {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0
    }
}));

// Container Components
export const IconContainer = styled(Box)(() => ({
    width: "100%",
    display: "flex",
    gap: "12px",
    justifyContent: "flex-start",
    fontSize: "20px"
}));

// Search Components
export const SearchField = styled(TextField)(() => ({
    height: inputHeight,
    "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: "6px",
    }
}));

// Chip Components
export const StyledChip = styled(Chip)(() => ({
    padding: "8px",
}));

// Accordion Components
export const CollapsableTab = styled(Accordion)(() => ({
    "&.MuiAccordion-root:before": {
        backgroundColor: "white"
    }
}));

// Dropzone Components
export const Dropzone = styled(Box)(() => ({
    borderRadius: "4px",
    border: "1px dashed #92ABFA",
    cursor: "pointer",
}));

// Table sx props (for MUI Table components)
export const tableRowSx = {
    '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
    '&:nth-of-type(even)': { backgroundColor: '#F9F9F9' }
};

export const tableCellNoBorderSx = {
    border: 'none'
};

export const tableCellTruncateSx = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '250px'
};

// Table Components
export const Table = styled('table')(() => ({
    backgroundColor: "white",
    borderRadius: "6px",
    minWidth: 650,
}));

export const TableCell = styled('td')(({ theme }) => ({
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "250px",
    padding: theme.spacing(1),
}));

export const TableHead = styled('thead')(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
}));

export const TableBody = styled('tbody')(() => ({}));

export const TableRow = styled('tr')(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:nth-of-type(even)': {
        backgroundColor: "#F9F9F9",
    },
}));

// Typography Components
export const FilterHeader = styled(Typography)(() => ({
    color: "#7F7D83",
    fontWeight: 700
}));

export const SectionTitle = styled(Typography)(() => ({
    fontSize: cardHeaderSize,
    fontWeight: 700,
}));

export const CardHeader = styled(Typography)(() => ({
    fontSize: cardHeaderSize,
    fontWeight: 700,
}));

export const DialogHeader = styled(Typography)(() => ({
    fontSize: dialogHeaderSize,
    fontWeight: 600,
}));

export const Root = styled(Box)(() => ({
    // maxWidth: "calc(100vw - 220px - 42px)"
    display: "flex",
    flexWrap: "wrap",
}));

export const Header = styled(Typography)(() => ({
    fontSize: headerSize,
    fontWeight: 600,
}));

export default theme;
